import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppShell } from '@/components/nav/AppShell';
import { propertiesApi, checklistApi, inventoryApi, uploadApi, uploadUrl } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { RefreshCw, Home as HomeIcon, MapPin, Package, ListChecks, Calendar as CalIcon, Plus, Trash2 } from 'lucide-react';
import { fmtDate } from '@/lib/format';
import { toast } from 'sonner';
import { PROPERTIES } from '@/constants/testIds';
import { Badge } from '@/components/ui/badge';
import { JobStatusPill } from '@/components/jobs/JobStatusPill';

export default function PropertyDetailPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [tplItems, setTplItems] = useState([]);
  const [invForm, setInvForm] = useState({ name: '', unit: 'unit', current_qty: 0, min_threshold: 0 });
  const [invOpen, setInvOpen] = useState(false);

  const load = async () => {
    const data = await propertiesApi.get(id);
    setP(data);
    setTplItems(data.checklist_template?.items || []);
  };
  useEffect(() => { load(); }, [id]);

  const sync = async () => {
    try {
      const r = await propertiesApi.syncIcal(id);
      toast.success(`Synced: ${r.jobs_created} created, ${r.jobs_updated} updated, ${r.jobs_cancelled} cancelled`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    }
  };

  const addTplItem = () => setTplItems([...tplItems, { id: `tmp_${Date.now()}`, title: '', description: '', photo_required: false }]);
  const updateTplItem = (idx, field, val) => setTplItems(tplItems.map((it, i) => i === idx ? { ...it, [field]: val } : it));
  const removeTplItem = (idx) => setTplItems(tplItems.filter((_, i) => i !== idx));
  const saveTemplate = async () => {
    const payload = { property_id: id, name: `${p.nickname} - Checklist`, items: tplItems.filter(it => (it.title || '').trim()) };
    try {
      if (p.checklist_template) {
        await checklistApi.updateTemplate(p.checklist_template.id, payload);
      } else {
        await checklistApi.createTemplate(payload);
      }
      toast.success('Checklist saved');
      load();
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const addInv = async (e) => {
    e.preventDefault();
    try {
      await inventoryApi.create({
        property_id: id,
        name: invForm.name,
        unit: invForm.unit,
        current_qty: parseFloat(invForm.current_qty) || 0,
        min_threshold: parseFloat(invForm.min_threshold) || 0,
      });
      toast.success('Inventory item added');
      setInvForm({ name: '', unit: 'unit', current_qty: 0, min_threshold: 0 });
      setInvOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const adjustInv = async (item, delta) => {
    await inventoryApi.adjust(item.id, delta);
    load();
  };

  if (!p) return <AppShell title="Property"><div className="text-sm text-muted-foreground">Loading…</div></AppShell>;

  return (
    <AppShell title={p.nickname}>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold flex items-center gap-2"><HomeIcon className="h-6 w-6 text-primary" />{p.nickname}</h1>
            {p.address && <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{p.address}</div>}
            <div className="mt-2 text-sm text-muted-foreground">{p.bedrooms} bd · {p.bathrooms} ba · {p.sqft ? `${p.sqft} sqft` : '—'} · TZ: {p.timezone}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {p.ical_url && (
              <Button onClick={sync} data-testid={PROPERTIES.syncButton}><RefreshCw className="h-4 w-4 mr-2" />Sync iCal now</Button>
            )}
            {p.last_ical_sync && <div className="text-xs text-muted-foreground">Last sync: {fmtDate(p.last_ical_sync)}</div>}
          </div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="reservations" data-testid={PROPERTIES.tabsReservations}>Reservations ({(p.reservations || []).length})</TabsTrigger>
            <TabsTrigger value="jobs">Jobs ({(p.jobs || []).length})</TabsTrigger>
            <TabsTrigger value="checklist" data-testid={PROPERTIES.tabsChecklist}>Checklist</TabsTrigger>
            <TabsTrigger value="inventory" data-testid={PROPERTIES.tabsInventory}>Inventory ({(p.inventory || []).length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <Card>
              <CardContent className="p-4 space-y-2 text-sm">
                <div><span className="text-muted-foreground">iCal URL:</span> {p.ical_url ? <span className="font-mono text-xs break-all">{p.ical_url}</span> : <span className="text-muted-foreground">Not configured</span>}</div>
                <div><span className="text-muted-foreground">Buffer after checkout:</span> {p.buffer_after_hours}h</div>
                <div><span className="text-muted-foreground">Default cleaning duration:</span> {p.default_cleaning_hours}h</div>
                {p.notes && <div><span className="text-muted-foreground">Notes:</span> {p.notes}</div>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reservations" className="mt-4">
            <div className="space-y-2">
              {(p.reservations || []).length === 0 ? (
                <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">No reservations yet. {p.ical_url ? 'Try syncing iCal.' : 'Configure the iCal URL to import.'}</CardContent></Card>
              ) : (
                (p.reservations || []).map(r => (
                  <div key={r.uid} className="border rounded-lg p-3 bg-card">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium truncate">{r.summary || 'Reservation'}</div>
                      <Badge variant={r.is_blocked ? 'secondary' : 'outline'} className="text-[10px]">{r.is_blocked ? 'Blocked' : (r.status || 'confirmed')}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1"><CalIcon className="h-3 w-3 inline mr-1" />{fmtDate(r.start)} → {fmtDate(r.end)}</div>
                    <div className="text-[11px] font-mono text-muted-foreground mt-1">{r.uid}</div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="jobs" className="mt-4">
            <div className="space-y-2">
              {(p.jobs || []).length === 0 ? <div className="text-sm text-muted-foreground">No jobs at this property yet.</div> :
                (p.jobs || []).map(j => (
                  <Link key={j.id} to={`/jobs/${j.id}`} className="flex items-center justify-between border rounded-lg p-3 bg-card hover:bg-muted/40">
                    <div>
                      <div className="font-medium text-sm">Job {j.id.slice(0, 6)}</div>
                      <div className="text-xs text-muted-foreground">{fmtDate(j.scheduled_at)}</div>
                    </div>
                    <JobStatusPill status={j.status} />
                  </Link>
                ))
              }
            </div>
          </TabsContent>

          <TabsContent value="checklist" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><ListChecks className="h-4 w-4" />Checklist template</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={addTplItem}><Plus className="h-3 w-3 mr-1" />Add item</Button>
                  <Button size="sm" onClick={saveTemplate}>Save</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {tplItems.length === 0 && <div className="text-sm text-muted-foreground">No checklist items yet. Add tasks that your cleaner should complete for this property.</div>}
                {tplItems.map((it, idx) => (
                  <div key={it.id || idx} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input placeholder="Task title" value={it.title || ''} onChange={e => updateTplItem(idx, 'title', e.target.value)} className="flex-1" />
                      <Button variant="ghost" size="icon" onClick={() => removeTplItem(idx)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    <Textarea placeholder="Description / instructions" value={it.description || ''} onChange={e => updateTplItem(idx, 'description', e.target.value)} rows={2} />
                    <div className="flex items-center gap-2 text-sm">
                      <Switch checked={!!it.photo_required} onCheckedChange={v => updateTplItem(idx, 'photo_required', v)} />
                      Photo required
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="inventory" className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-muted-foreground">Track supplies and get alerts when they run low.</div>
              <Dialog open={invOpen} onOpenChange={setInvOpen}>
                <DialogTrigger asChild>
                  <Button size="sm"><Plus className="h-3 w-3 mr-1" />Add item</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add inventory item</DialogTitle></DialogHeader>
                  <form onSubmit={addInv} className="space-y-3">
                    <div><Label>Item name *</Label><Input required placeholder="Toilet paper rolls" value={invForm.name} onChange={e => setInvForm({ ...invForm, name: e.target.value })} /></div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div><Label>Unit</Label><Input value={invForm.unit} onChange={e => setInvForm({ ...invForm, unit: e.target.value })} /></div>
                      <div><Label>Current</Label><Input type="number" step="0.1" value={invForm.current_qty} onChange={e => setInvForm({ ...invForm, current_qty: e.target.value })} /></div>
                      <div><Label>Min threshold</Label><Input type="number" step="0.1" value={invForm.min_threshold} onChange={e => setInvForm({ ...invForm, min_threshold: e.target.value })} /></div>
                    </div>
                    <DialogFooter><Button type="submit">Add</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-2">
              {(p.inventory || []).map(it => {
                const low = it.current_qty <= it.min_threshold;
                return (
                  <div key={it.id} className={`border rounded-lg p-3 flex items-center justify-between bg-card ${low ? 'border-[hsl(var(--fc-warning)/0.6)]' : ''}`}>
                    <div>
                      <div className="font-medium text-sm flex items-center gap-2"><Package className="h-4 w-4" />{it.name} {low && <Badge variant="outline" className="text-[10px] text-[hsl(var(--fc-warning))]">Low</Badge>}</div>
                      <div className="text-xs text-muted-foreground">Threshold: {it.min_threshold} {it.unit}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="icon" variant="outline" onClick={() => adjustInv(it, -1)}>-</Button>
                      <div className="w-16 text-center tabular-nums font-semibold">{it.current_qty} {it.unit}</div>
                      <Button size="icon" variant="outline" onClick={() => adjustInv(it, 1)}>+</Button>
                    </div>
                  </div>
                );
              })}
              {(!p.inventory || p.inventory.length === 0) && <div className="text-sm text-muted-foreground">No inventory items yet.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
